// Pure rules behind the role screens (no imports, so Node's test runner loads it as is).
// Strings returned here are English i18n keys; the screens translate them.

/* ---------------- Web and support roles ---------------- */

export interface RoleModule {
  id: string;
  name: string;
  value: boolean;
}

export interface RoleGroup {
  id: string | number;
  name: string;
  value: RoleModule[];
}

export interface RoleForm {
  roleName: string;
  roleDescription: string;
  restrictionDescription: string;
  roleHierarchy: string;
  loadModuleId: string;
}

export type RoleScope = "tenant" | "support" | "platform";

export const EMPTY_ROLE_FORM: RoleForm = {
  roleName: "",
  roleDescription: "",
  restrictionDescription: "",
  roleHierarchy: "",
  loadModuleId: "",
};

const norm = (value: unknown) => String(value ?? "").trim().toLowerCase();

// Child module (lower-case) -> the module it needs; the host gates these pages by Company Details,
// and approving a stock count happens on the Stock Counts pages.
export const MODULE_DEPENDENCIES: Record<string, string> = {
  billing: "company details",
  users: "company details",
  "invoice configurations": "company details",
  "stock count approval": "stock counts",
};

// Groups whose pages show money or sales, tagged in the editor.
export const GROUP_DATA_TAGS: Record<string, string> = {
  finance: "Financial data",
  report: "Sales data",
};

export const allModules = (groups: RoleGroup[]): RoleModule[] => groups.flatMap((g) => g.value ?? []);

export const countOn = (groups: RoleGroup[]) => allModules(groups).filter((m) => m.value).length;

export const countAll = (groups: RoleGroup[]) => allModules(groups).length;

// Mirrors core-api src/guards/support-gate.ts (MOD plus the reservations pair): the only modules a support token is ever served.
export const SUPPORT_GATE_MODULES: string[] = [
  "items", "quick add items", "attributes", "product modifiers", "promotion", "reasons", "schedules",
  "sale points", "floor plans", "locations", "reservation setups", "devices", "device tender", "device health",
  "device users", "order templates", "stock counts", "corrections", "transfers", "supplier inventory",
  "customer orders", "clients", "employees", "invoice configurations", "table reservations", "reservations",
];

// No host route for these, or (Device Health) a 403 inside a customer company.
const SUPPORT_NOT_LANDING = new Set(["quick add items", "invoice configurations", "device health"]);

/** A support role can hold a module the gate serves, unless its parent page is one the gate never serves. */
export const isSupportModule = (module: RoleModule): boolean => {
  const name = norm(module.name);
  const parent = MODULE_DEPENDENCIES[name];
  return SUPPORT_GATE_MODULES.includes(name) && (!parent || SUPPORT_GATE_MODULES.includes(parent));
};

/** The modules a role of this scope can hold; empty groups drop out. */
export const scopeGroups = (groups: RoleGroup[], scope: RoleScope = "tenant"): RoleGroup[] =>
  scope !== "support"
    ? groups
    : groups.map((g) => ({ ...g, value: (g.value ?? []).filter(isSupportModule) })).filter((g) => g.value.length > 0);

/** Switched-on modules a role of this scope cannot use; saving switches them off. */
export const unusableModules = (groups: RoleGroup[], scope: RoleScope = "tenant"): RoleModule[] =>
  scope !== "support" ? [] : allModules(groups).filter((m) => m.value && !isSupportModule(m));

/** A member role with no saved pages gets every page at sign-in (core-api getUserByToken); a support role gets none. */
export const implicitAllModules = (
  role: { roleScope?: unknown; modulesImplicitAll?: unknown } | null | undefined,
  onCount: number,
  totalCount: number,
): boolean => {
  if (!role || role.roleScope === "support") return false;
  if (typeof role.modulesImplicitAll === "boolean") return role.modulesImplicitAll;
  return totalCount > 0 && onCount === 0;
};

// Granted only by a saved switch: a role with no saved pages does not hold these (inventory-api checks the map).
export const EXPLICIT_ONLY_MODULES = new Set(["stock count approval"]);

/** A loaded role's switches as its holders really have them, so a save keeps today's access. */
export const savedAccessGroups = (role: any, groups: RoleGroup[]): RoleGroup[] =>
  implicitAllModules(role, countOn(groups), countAll(groups))
    ? groups.map((g) => ({ ...g, value: (g.value ?? []).map((m) => ({ ...m, value: !EXPLICIT_ONLY_MODULES.has(norm(m.name)) })) }))
    : groups;

/** The module this one depends on, wherever it sits. */
export const parentOf = (groups: RoleGroup[], module: RoleModule): RoleModule | undefined => {
  const parentName = MODULE_DEPENDENCIES[norm(module.name)];
  if (!parentName) return undefined;
  return allModules(groups).find((m) => norm(m.name) === parentName && m.id !== module.id);
};

export const isLocked = (groups: RoleGroup[], module: RoleModule) => {
  const parent = parentOf(groups, module);
  return !!parent && !parent.value;
};

const mapModules = (groups: RoleGroup[], fn: (m: RoleModule, g: RoleGroup) => RoleModule): RoleGroup[] =>
  groups.map((g) => ({ ...g, value: (g.value ?? []).map((m) => fn(m, g)) }));

/** A child whose parent is off is off. */
export const enforceDependencies = (groups: RoleGroup[]): RoleGroup[] =>
  mapModules(groups, (m) => (m.value && isLocked(groups, m) ? { ...m, value: false } : m));

export const setModule = (groups: RoleGroup[], moduleId: string, value: boolean): RoleGroup[] => {
  const target = allModules(groups).find((m) => m.id === moduleId);
  if (!target || (value && isLocked(groups, target))) return groups;
  return enforceDependencies(mapModules(groups, (m) => (m.id === moduleId ? { ...m, value } : m)));
};

export const setGroup = (groups: RoleGroup[], groupId: string | number, value: boolean): RoleGroup[] => {
  const switched = mapModules(groups, (m, g) => (g.id === groupId ? { ...m, value } : m));
  return enforceDependencies(switched);
};

export const groupState = (group: RoleGroup): "all" | "some" | "none" => {
  const on = (group.value ?? []).filter((m) => m.value).length;
  if (on === 0) return "none";
  return on === (group.value ?? []).length ? "all" : "some";
};

/** Takes another role's switches, module by module; modules it does not list go off. */
export const copyModuleValues = (target: RoleGroup[], source: RoleGroup[]): RoleGroup[] => {
  const sourceValues = new Map(allModules(source).map((m) => [m.id, !!m.value]));
  return enforceDependencies(mapModules(target, (m) => ({ ...m, value: sourceValues.get(m.id) ?? false })));
};

export interface ModuleChanges {
  added: RoleModule[];
  removed: RoleModule[];
}

export const moduleChanges = (initial: RoleGroup[], current: RoleGroup[]): ModuleChanges => {
  const before = new Map(allModules(initial).map((m) => [m.id, !!m.value]));
  const added: RoleModule[] = [];
  const removed: RoleModule[] = [];
  for (const m of allModules(current)) {
    const was = before.get(m.id) ?? false;
    if (m.value && !was) added.push(m);
    if (!m.value && was) removed.push(m);
  }
  return { added, removed };
};

/** True when the save would switch 'Roles' off on the viewer's own role, locking them out of this screen. */
export const dropsOwnRolesAccess = (role: { isOwnRole?: unknown } | null | undefined, initial: RoleGroup[], current: RoleGroup[]) =>
  role?.isOwnRole === true && moduleChanges(initial, current).removed.some((m) => norm(m.name) === "roles");

const DETAIL_LABELS: { key: keyof RoleForm; label: string }[] = [
  { key: "roleName", label: "Name" },
  { key: "loadModuleId", label: "Landing page" },
  { key: "roleDescription", label: "Description" },
  { key: "roleHierarchy", label: "Rank" },
  { key: "restrictionDescription", label: "Notes" },
];

/** Labels of the detail fields that differ, in screen order. */
export const detailChanges = (initial: RoleForm, current: RoleForm): string[] =>
  DETAIL_LABELS.filter(({ key }) => String(initial[key] ?? "").trim() !== String(current[key] ?? "").trim()).map(
    ({ label }) => label,
  );

export interface LandingOption {
  id: string;
  groupName: string;
  moduleName: string;
}

/** Only modules switched on can be the landing page; a support role also needs one staff can open. */
export const landingOptions = (groups: RoleGroup[], scope: RoleScope = "tenant"): LandingOption[] =>
  scopeGroups(groups, scope).flatMap((g) =>
    (g.value ?? [])
      .filter((m) => m.value && (scope !== "support" || !SUPPORT_NOT_LANDING.has(norm(m.name))))
      .map((m) => ({ id: m.id, groupName: g.name, moduleName: m.name })),
  );

export const isLandingValid = (groups: RoleGroup[], loadModuleId: string, scope: RoleScope = "tenant") =>
  !!loadModuleId && landingOptions(groups, scope).some((o) => o.id === loadModuleId);

/** Landing page after a switch change: cleared when its page goes off, the first option once the first page goes on. */
export const nextLandingId = (currentId: string, before: LandingOption[], after: LandingOption[]): string => {
  if (currentId) return before.some((o) => o.id === currentId) && !after.some((o) => o.id === currentId) ? "" : currentId;
  return before.length === 0 && after.length > 0 ? after[0].id : "";
};

/** '' -> no rank; digits -> the number; anything else -> NaN (a validation error). */
export const parseRank = (value: unknown): number | undefined => {
  const raw = String(value ?? "").trim();
  if (raw === "") return undefined;
  return /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
};

export const validateRole = (form: RoleForm, groups: RoleGroup[], scope: RoleScope = "tenant"): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!form.roleName.trim()) errors.roleName = "Role name is required";
  if (!form.roleDescription.trim()) errors.roleDescription = "Description is required";
  if (countOn(scopeGroups(groups, scope)) === 0) errors.modules = "Turn on at least one page before saving.";
  else if (!isLandingValid(groups, form.loadModuleId, scope)) errors.loadModuleId = "Choose a landing page that is switched on";
  if (Number.isNaN(parseRank(form.roleHierarchy))) errors.roleHierarchy = "Rank must be a whole number";
  return errors;
};

/** The POST/PUT body: exactly the API's fields. The API takes the editor from the session, never the body. */
export const buildRolePayload = (
  form: RoleForm,
  groups: RoleGroup[],
  options: { isNew: boolean; roleScope?: RoleScope },
): Record<string, unknown> => {
  const support = options.roleScope === "support";
  const payload: Record<string, unknown> = {
    roleName: form.roleName.trim(),
    roleDescription: form.roleDescription.trim(),
    restrictionDescription: form.restrictionDescription.trim(),
    loadModuleId: form.loadModuleId,
    modules: allModules(groups).map((m) => ({ id: m.id, value: !!m.value && (!support || isSupportModule(m)) })),
  };
  const rank = parseRank(form.roleHierarchy);
  if (rank !== undefined && !Number.isNaN(rank)) payload.roleHierarchy = rank;
  if (options.isNew) payload.roleScope = options.roleScope ?? "tenant";
  return payload;
};

/** A loaded role as the form's text fields. */
export const roleToForm = (role: any): RoleForm => {
  const rank = role?.roleHierarchy ?? role?.roleHirerarchy;
  return {
    roleName: String(role?.roleName ?? ""),
    roleDescription: String(role?.roleDescription ?? ""),
    restrictionDescription: String(role?.restrictionDescription ?? ""),
    roleHierarchy: rank === null || rank === undefined ? "" : String(rank),
    loadModuleId: String(role?.loadModuleId ?? ""),
  };
};

export const isRoleScope = (value: unknown): value is RoleScope =>
  value === "tenant" || value === "support" || value === "platform";

/** core-api refuses to rename or delete a role the system uses by name, so neither is offered. */
export const isSystemRole = (role: { isSystemRole?: unknown } | null | undefined): boolean => role?.isSystemRole === true;

/** The delete dialog's holder line: a support role's count is InnovX staff across every company, not users here. */
export const deleteHoldersKey = (role: { roleScope?: unknown } | null | undefined, count: number): string => {
  const support = role?.roleScope === "support";
  if (count <= 0) return support ? "No InnovX staff have this role." : "No users in this company have this role.";
  if (!support) return "{n} users in this company have {name}. Move them to another role before deleting it.";
  return count === 1
    ? "1 InnovX staff member has {name}. Move them to another role before deleting it."
    : "{n} InnovX staff have {name}. Move them to another role before deleting it.";
};

/* ---------------- Device roles ---------------- */

export interface DeviceFunction {
  /** The stored id: the exact English label the till matches. Never change it. */
  id: string;
  /** Display key when it differs from the id. */
  label?: string;
  description?: string;
  tillName?: string;
  badges?: string[];
  comingSoon?: boolean;
}

export interface DeviceSection {
  key: string;
  title: string;
  functions: DeviceFunction[];
}

const NOT_CHECKED = "Not checked by the till yet";

export const DEVICE_SECTIONS: DeviceSection[] = [
  {
    key: "selling",
    title: "Selling",
    functions: [
      { id: "Voids", description: "Remove or reduce items that were already printed or sent. Without it, only unprinted items can be removed." },
      { id: "Clear basket", description: "Empty the whole basket in one action." },
      {
        id: "Price override",
        description: "Swipe a line to change its price for this sale only. The product's price is not changed. Regular-priced items only.",
      },
      {
        id: "Apply discount",
        description: "Apply any manual discount without a PIN and approve other staff's discounts.",
        badges: ["Coming soon"],
        comingSoon: true,
      },
    ],
  },
  {
    key: "payments",
    title: "Payments",
    functions: [
      {
        id: "Edit service charge",
        description: "Change the service charge percentage or set a fixed amount on the payment screen. Removing an optional charge needs no permission.",
      },
      { id: "Print bill", description: "Print the bill from the order header before payment." },
      { id: "Refunds", description: "Open Refunds from the menu to refund a sale." },
      {
        id: "Card Payment Settings",
        description: "Set up this till's card machine: provider, terminal ID and slip printing.",
        badges: ["New"],
      },
    ],
  },
  {
    key: "tables",
    title: "Tables and orders",
    functions: [
      { id: "View open tables", description: "Open tables started by other staff. Without it, staff see only their own." },
      { id: "Move order", description: "Move an order to another table." },
      {
        id: "Transfer table owner",
        description: "Hand any table, including unowned ones, to another staff member. Staff can always hand over their own.",
      },
      { id: "Take-away orders", description: "Create, collect and cancel take-away orders.", badges: [NOT_CHECKED] },
    ],
  },
  {
    key: "cash",
    title: "Cash and shift",
    functions: [
      { id: "Open cash-drawer", label: "Open cash drawer", description: "Open the drawer from the menu without a sale." },
      { id: "Transactions", description: "Open the Transactions list from the menu." },
      { id: "Revenue", description: "Open the Revenue (sales summary) page." },
      { id: "End your shift", description: "Show 'End shift' at the bottom of the menu." },
      { id: "End of day", description: "Show 'End of day' at the bottom of the menu." },
      {
        id: "Fiscal functions",
        description: "Print X and Z reports and reprint the last receipt.",
        badges: ["Shown to everyone while fiscal is on"],
      },
    ],
  },
  {
    key: "reservations",
    title: "Reservations",
    functions: [
      { id: "Reservations", description: "View and manage bookings.", badges: ["Shown to everyone"] },
      {
        id: "Reservation control",
        description: "Pause reservations, manage blackout periods and override capacity from the till.",
      },
    ],
  },
  {
    key: "till",
    title: "Till and admin",
    functions: [
      {
        id: "Change sale point",
        description: "Switch which sale point this till sells for.",
        tillName: "Shown on the till as Locations",
      },
      { id: "Sync", description: "Run a manual sync from the menu." },
      { id: "Sync state", badges: [NOT_CHECKED] },
      {
        id: "Print settings",
        description: "Open Print settings. The pending-prints badge also leads here when Print history is not granted.",
      },
      { id: "Stock check", description: "Open the Stock check page." },
      { id: "Print history", description: "Open Print history.", badges: ["Coming soon"], comingSoon: true },
    ],
  },
];

/** Grantable function ids, in screen order (Coming soon rows excluded: they are never sent). */
export const DEVICE_FUNCTION_IDS: string[] = DEVICE_SECTIONS.flatMap((s) =>
  s.functions.filter((f) => !f.comingSoon).map((f) => f.id),
);

const CATALOGUE_IDS = new Set(DEVICE_SECTIONS.flatMap((s) => s.functions.map((f) => f.id)));

// The map row the API uses for promotions; never a function.
export const PROMOTION_MARKER = "Promotion";

/** Stored function ids split into the ones this page shows and the ones it must keep untouched. */
export const splitStoredFunctions = (storedIds: unknown[]): { known: string[]; unknown: string[] } => {
  const known: string[] = [];
  const unknown: string[] = [];
  for (const raw of storedIds) {
    if (typeof raw !== "string" || raw.trim() === "" || raw === PROMOTION_MARKER) continue;
    if (DEVICE_FUNCTION_IDS.includes(raw)) {
      if (!known.includes(raw)) known.push(raw);
    } else if (!CATALOGUE_IDS.has(raw) && !unknown.includes(raw)) {
      unknown.push(raw);
    }
  }
  return { known, unknown };
};

/** Ticked functions in catalogue order, then the stored ones this page does not know, unchanged. */
export const buildDeviceFunctions = (checked: string[], keptUnknown: string[]): string[] => {
  const ticked = DEVICE_FUNCTION_IDS.filter((id) => checked.includes(id));
  return [...ticked, ...keptUnknown.filter((id) => !ticked.includes(id))];
};

export interface DeviceForm {
  roleName: string;
  roleDescription: string;
  restrictionDescription: string;
  roleHirerarchy: string;
}

export const EMPTY_DEVICE_FORM: DeviceForm = {
  roleName: "",
  roleDescription: "",
  restrictionDescription: "",
  roleHirerarchy: "",
};

export const validateDeviceRole = (form: DeviceForm): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!form.roleName.trim()) errors.roleName = "Role name is required";
  const rank = parseRank(form.roleHirerarchy);
  if (rank === undefined) errors.roleHirerarchy = "Sort order is required";
  else if (Number.isNaN(rank)) errors.roleHirerarchy = "Sort order must be a whole number";
  else if (rank < 1) errors.roleHirerarchy = "Sort order must be 1 or more";
  return errors;
};

export interface DevicePromotionState {
  /** True only once the picker has the role's assigned ids AND the full promotion list. */
  loaded: boolean;
  selectedIds: string[];
  /** Assigned ids the picker does not show (not a manual discount, or deleted): kept as they are. */
  hiddenAssignedIds: string[];
}

/** Omitting promotionIds leaves the role's discounts untouched, so it is only sent once loaded. */
export const buildDevicePayload = (
  form: DeviceForm,
  functions: { checked: string[]; keptUnknown: string[] },
  promotions: DevicePromotionState,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    roleName: form.roleName.trim(),
    roleDescription: form.roleDescription.trim(),
    restrictionDescription: form.restrictionDescription.trim(),
    roleHirerarchy: parseRank(form.roleHirerarchy),
    deviceUserRolePermission: buildDeviceFunctions(functions.checked, functions.keptUnknown),
  };
  if (promotions.loaded) {
    const ids = [...promotions.selectedIds, ...promotions.hiddenAssignedIds];
    payload.promotionIds = ids.filter((id, index) => typeof id === "string" && id !== "" && ids.indexOf(id) === index);
  }
  return payload;
};

export interface PromotionOption {
  id: string;
  name: string;
  isActive: boolean;
  value?: unknown;
  discountType?: unknown;
}

/** Only manual discounts (promoType 2) are checked against the role on the till. */
export const manualDiscounts = (promotions: any[]): PromotionOption[] =>
  (promotions ?? [])
    .filter((p) => p && typeof p.id === "string" && Number(p.promoType) === 2)
    .map((p) => ({
      id: p.id,
      name: String(p.promoName ?? ""),
      isActive: p.isActive !== false,
      value: p.value,
      discountType: p.discountType,
    }));

export const splitAssignedPromotions = (assignedIds: string[], shown: PromotionOption[]) => {
  const shownIds = new Set(shown.map((p) => p.id));
  return {
    selected: assignedIds.filter((id) => shownIds.has(id)),
    hidden: assignedIds.filter((id) => !shownIds.has(id)),
  };
};

/** Next free sort order for a new device role. */
export const nextSortOrder = (roles: { roleHirerarchy?: unknown }[]): number => {
  const ranks = (roles ?? []).map((r) => Number(r?.roleHirerarchy)).filter((n) => Number.isFinite(n));
  return ranks.length ? Math.max(...ranks) + 1 : 1;
};

/** Reads every page until `count` rows (or an empty page); `maxPages` stops a runaway loop. */
export const fetchAllPages = async <T>(
  fetchPage: (page: number, limit: number) => Promise<{ rows: T[]; count: number }>,
  limit = 100,
  maxPages = 200,
): Promise<T[]> => {
  const rows: T[] = [];
  for (let page = 1; page <= maxPages; page++) {
    const result = await fetchPage(page, limit);
    rows.push(...(result.rows ?? []));
    if ((result.rows ?? []).length === 0 || rows.length >= (result.count ?? 0)) break;
  }
  return rows;
};
